import type {
  Action,
  Bank,
  CrbServiceProvider,
  Dispensary,
  Group,
  MinifiedDispensary,
  MinifiedUser,
  MUO,
  QuestionnaireResultResponse,
  SubFeaturesFinCen,
  SubFeaturesMonitoring,
  TemplateResponse,
  TemplateResponseCore,
  TemplateResultResponse,
  User
} from '@gcv/shared';
import {
  CoreTransactionPaymentType,
  CustomFieldType,
  MainFeatures,
  NonCannabisIndustryTypes,
  OrganizationRoleResolver,
  PosType,
  RequirementType,
  UserInterests
} from '@gcv/shared';

import { BankRoleOptions, DispensaryRoleOptions } from 'domain/consts/roles';
import type { PartialDispensary } from 'domain/types/gcv/partialDispensary';
import { getCrbDispensaryStore } from 'stores/CrbDispensaryStore';
import { getFiBankStore } from 'stores/FiBankStore';
import { getFiTemplateStore } from 'stores/FiTemplateStore';
import { shouldDisplayFieldWithSmartRule } from 'ui/organisms/CustomFormGenerator/custom-form-generator.organism';
import { getCustomFieldsFormValues } from './additional-info.util';
import { DateTimeHelpers } from './dateTime.util';
import { isOnboardingDocumentRequirementFulfilled } from './due-diligence.util';

export enum OrganizationType {
  Bank = 'BANK',
  Dispensary = 'DISPENSARY'
}

/**
 * Model properties common to FI or CRB organizations.
 */
export class Organization {
  public organizationType: OrganizationType;

  /**
   * Return FI instance.
   * @returns organization instance modeling a financial institution (FI).
   */
  public static getBank = () => {
    return new this(OrganizationType.Bank);
  };

  /**
   * Return CRB instance.
   * @returns organization instance modeling a cannabis-related business (CRB).
   */
  public static getDispensary = () => {
    return new this(OrganizationType.Dispensary);
  };

  private constructor(organizationType: OrganizationType) {
    this.organizationType = organizationType;
  }

  public getAbbreviation = () => {
    return this.organizationType === OrganizationType.Bank ? 'fi' : 'crb';
  };

  public getStore = () => {
    return this.organizationType === OrganizationType.Bank ? getFiBankStore() : getCrbDispensaryStore();
  };

  public getStaff = () => {
    return this.organizationType === OrganizationType.Bank
      ? getFiBankStore().staff
      : getCrbDispensaryStore().currentDispensaryStaff;
  };

  public isStaffLoaded = () => {
    return this.organizationType === OrganizationType.Bank
      ? getFiBankStore().isStaffLoaded
      : getCrbDispensaryStore().isStaffLoaded;
  };

  public getTimezone = () => {
    return this.organizationType === OrganizationType.Bank
      ? getFiBankStore().bank.iana_timezone
      : getCrbDispensaryStore().currentDispensary.iana_timezone;
  };

  public getId = () => {
    return this.organizationType === OrganizationType.Bank
      ? getFiBankStore().bank.id
      : getCrbDispensaryStore().currentDispensary.id;
  };

  public getAdminRole = () => {
    return this.organizationType === OrganizationType.Bank ? 'bank_admin' : 'dispensary_admin';
  };

  public getStandardRole = () => {
    return this.organizationType === OrganizationType.Bank ? 'bank_user' : 'dispensary_user';
  };

  public getRoleModifier = () => {
    return this.organizationType === OrganizationType.Bank ? 'bank_' : 'dispensary_';
  };

  public getRoleOptions = () => {
    return this.organizationType === OrganizationType.Bank ? BankRoleOptions : DispensaryRoleOptions;
  };
}

export const checkPermissions = (permissions: Action[], user: User, organization: Bank | Dispensary) => {
  const resolver = new OrganizationRoleResolver();

  let hasPermission = false;
  if (permissions && user && organization) {
    permissions.forEach((permission: Action) => {
      if (resolver.userCanDoAction(organization.groups || [], user, permission)) {
        hasPermission = true;
        return;
      }
      return;
    });
    return hasPermission;
  } else {
    return hasPermission;
  }
};

export const getLastLoginForOrg = (
  org: PartialDispensary | Bank,
  staffDictionary: { [k: string]: MinifiedUser }
): number => {
  const orgStaff: MinifiedUser[] =
    org.orgType === 'bank'
      ? getStaffUserObjectsForBank(org as Bank, staffDictionary)
      : getStaffUserObjectsForPartialDispensary(org as PartialDispensary, staffDictionary);

  if (orgStaff.length > 0) {
    const lastLoginUnixTimestamp = orgStaff.reduce((latestTime, currentUser) => {
      if (currentUser && currentUser.ll) {
        const currentUserLastLogin = DateTimeHelpers.parseFromISOString(currentUser.ll, 'utc').valueOf();

        if (latestTime < currentUserLastLogin) {
          latestTime = currentUserLastLogin;
        }
        return latestTime;
      } else {
        return latestTime;
      }
    }, 0);

    if (lastLoginUnixTimestamp > 0) {
      return lastLoginUnixTimestamp;
    }
  }
  return 0;
};

const getStaffUserObjectsForPartialDispensary = (
  org: PartialDispensary,
  staffDictionary: { [k: string]: MinifiedUser }
): MinifiedUser[] => {
  return org.userList.reduce((acc, currentUserId) => {
    const user = staffDictionary[currentUserId];

    if (user) {
      acc = [...acc, user];
    }
    return acc;
  }, [] as MinifiedUser[]);
};

const getStaffUserObjectsForBank = (
  org: Bank,
  staffDictionary: { [k: string]: MinifiedUser }
): MinifiedUser[] => {
  return getStaffIdsForOrg(org).reduce((acc, currentUserId) => {
    const user = staffDictionary[currentUserId];

    if (user) {
      acc = [...acc, user];
    }
    return acc;
  }, [] as MinifiedUser[]);
};

const getStaffIdsForOrg = (org: Dispensary | Bank): string[] => {
  if (org.groups) {
    return org.groups.reduce((acc, curr) => {
      if (curr.users) {
        curr.users.forEach((userId) => {
          if (!acc.includes(userId)) {
            acc = [...acc, userId];
          }
        });
      }

      return acc;
    }, [] as string[]);
  }

  return [];
};

export const getDispensaryType = (posName: PosType): 'retail' | 'non_retail' => {
  if (
    posName === 'Quickbooks' ||
    posName === 'GcvCsv_Quickbooks' ||
    posName === 'MjFreewayWholesale' ||
    posName === 'BioTrackSqlWholesale' ||
    posName === 'LeafLogixWholesale' ||
    posName === PosType.MetrcS2SWholesale
  ) {
    return 'non_retail';
  } else {
    return 'retail';
  }
};

export const isGroupLocked = (group: Group): boolean => group.type !== 'user_created' && !!group.type;

export const getDispensaryMotUsers = (
  dispensary: Dispensary,
  dispensaryUsers: User[]
):
  | {
      id: string;
      name: string;
      favorite: boolean;
      active: boolean;
      type: 'user';
    }[] => {
  const returnVal: {
    id: string;
    name: string;
    favorite: boolean;
    active: boolean;
    type: 'user';
  }[] = [];
  return dispensary.methodOfTransportation.user.reduce((filteredUsers, val) => {
    const motUser = dispensaryUsers.find((user) => user.id === val.id);
    if (motUser && motUser.invitation_status !== 'archived' && motUser.identityVerified) {
      const user = { ...val, name: `${motUser.firstName} ${motUser.lastName}` };
      filteredUsers.push({ ...user, type: 'user' });
    }
    return filteredUsers;
  }, returnVal);
};

export const getAvailableMethodOfTransportationForDeposit = (
  dispensary: Dispensary,
  dispensaryUsers: User[],
  bank?: CrbServiceProvider
): {
  id: string;
  name: string;
  favorite: boolean;
  active: boolean;
  type: 'bank' | 'user' | 'vendor';
}[] => {
  // check for a bank favorite MOT, if we have any return those as the only options
  // bank CrbServiceProvider object will return account specific favorite mot over fi global favorite
  if (bank) {
    const bankRequired = bank.transportVendors.filter((mot) => mot.active && mot.favorite);
    if (bankRequired.length) {
      return bankRequired.map((mot) => {
        return {
          ...mot,
          active: mot.active === true,
          favorite: mot.favorite === true,
          type: 'bank' as 'bank' | 'user' | 'vendor'
        };
      });
    }
  }
  // get MOT users with shared function to filter out archived and use user's name
  const motUsers = getDispensaryMotUsers(dispensary, dispensaryUsers);
  const bankMots = bank
    ? bank.transportVendors
        .filter((mot) => mot.active)
        .map((mot) => {
          return {
            ...mot,
            active: mot.active === true,
            favorite: mot.favorite === true,
            type: 'bank' as 'bank' | 'user' | 'vendor'
          };
        })
    : [];
  const vendorMots = dispensary.methodOfTransportation.vendor
    .filter((mot) => mot.active)
    .map((mot) => {
      return { ...mot, type: 'vendor' as 'bank' | 'user' | 'vendor' };
    });
  return [...motUsers, ...bankMots, ...vendorMots];
};

export const getLicenseType = (minDisp: MinifiedDispensary) => {
  let licenseType = '--';

  if (minDisp) {
    if (NonCannabisIndustryTypes.includes(minDisp.industryType)) {
      return '--';
    }

    if (minDisp.is_tribal_nation) {
      return 'Tribal';
    }

    if (
      !getFiTemplateStore().templates.onboarding[minDisp.assigned_onboarding_template.template_id]
        ?.plant_touching
    ) {
      licenseType = 'None (non-licensed)';
    } else {
      licenseType = minDisp.assigned_onboarding_template.license_type ?? '--';
    }
  }

  return licenseType;
};

export const isMailingAddressSame = (org: Dispensary | MUO): boolean => {
  return (
    org &&
    (!org.mailingStreetAddress ||
      (org.streetAddress === org.mailingStreetAddress &&
        org.city === org.mailingCity &&
        org.state === org.mailingState &&
        org.postalCode === (org.mailingPostalCode as unknown as string)))
  );
};

export const hasDispensaryFulfilledAllDocumentRequirements = (
  template: TemplateResponse,
  results: TemplateResultResponse,
  employeeLicensesRequired: boolean,
  businessLicenseRequired: boolean
): boolean => {
  let isFulfilled = true;

  // filter out archive requirements and employee licenses if not required
  const activeRequirements = Object.values(template.requirements).filter(
    (r) =>
      !r.archived &&
      (employeeLicensesRequired || r.type !== RequirementType.EmployeeLicense) &&
      (businessLicenseRequired || r.type !== RequirementType.BusinessLicense)
  );

  for (const requirement of activeRequirements) {
    if (!isFulfilled) break;
    const result = results.requirements_results.find((r) => r.requirement_id === requirement.requirement_id);
    if (!result || !isOnboardingDocumentRequirementFulfilled(result)) {
      isFulfilled = false;
    }
  }

  return isFulfilled;
};

export const hasDispensaryFulfilledAllAddtionalRequirements = (
  template: TemplateResponseCore,
  results: TemplateResultResponse | QuestionnaireResultResponse
): boolean => {
  const formValues = getCustomFieldsFormValues(results, template);

  //legacy check getting frontloaded away from logic
  const someResultDoesNotHaveResponses = results.custom_requirement_result.some((customRequirementResult) => {
    return customRequirementResult.custom_fields.responses == null;
  });
  if (someResultDoesNotHaveResponses) return false;
  const filteredRequirements = Object.values(template.custom_requirements).filter((s) => !s.archived);

  for (const requirement of filteredRequirements) {
    //gather all results for the requirement
    const resultsForRequirement = results.custom_requirement_result.filter((customReqResult) => {
      return customReqResult.requirement_id === requirement.requirement_id;
    });

    for (const resultForRequirement of resultsForRequirement) {
      //check if section should display
      if (
        shouldDisplayFieldWithSmartRule(
          requirement.custom_section.smart_rule,
          formValues,
          template,
          resultForRequirement.id
        )
      ) {
        //per result from above, additionally check each custom field on its requirement.
        //importantly, this choice of loop below builds required fields based on the source of truth requirement
        for (const customField of requirement.custom_section.fields) {
          if (
            shouldDisplayFieldWithSmartRule(
              customField.smart_rule,
              formValues,
              template,
              resultForRequirement.id
            )
          ) {
            if (customField.required && customField.type !== CustomFieldType.DisplayContent) {
              const responseValue = resultForRequirement.custom_fields.responses[customField.id];
              if (responseValue == null || responseValue?.length === 0) {
                return false;
              }
            }
          }
        }
      }
    }
  }
  return true;
};

// Function that takes the org (right now bank but dispensary can be added in the future) and features and
// returns if that feature is enabled or not
// if an org does not have a feature or subfeature defined this function will default to true
// e.g. orgFeatureEnabled(mybank, MainFeatures.FinCEN, SubFeatures.SAR) => true/false
export const orgFeatureEnabled = (
  org: Bank,
  parentFeature: MainFeatures,
  childFeature?: 'SAR' | 'CTR' | 'CoreTransactions'
) => {
  if (org.features && org.features[parentFeature]) {
    if (childFeature === undefined) {
      // we're looking for the parent feature
      return org.features[parentFeature]?.enabled;
    } else if (childFeature && org.features[parentFeature]?.sub_features === undefined) {
      // we didn't find the subfeature
      return true;
    } else if (parentFeature === MainFeatures.FinCEN) {
      return (org.features[parentFeature]?.sub_features as SubFeaturesFinCen)[childFeature as 'SAR' | 'CTR'];
    } else if (parentFeature === MainFeatures.Monitoring) {
      return (org.features[parentFeature]?.sub_features as SubFeaturesMonitoring)[
        childFeature as 'CoreTransactions'
      ];
    }
  }
  // either the features objet or the parent feature is not defined on the
  return true;
};

export const userInterestsReadableNameMap = {
  [UserInterests.AchAndWires]: 'ACH and Wires',
  [UserInterests.BillPay]: 'Bill Pay',
  [UserInterests.BusinessBanking]: 'Business Banking',
  [UserInterests.BusinessInsurance]: 'Business Insurance',
  [UserInterests.CashLogistics]: 'Cash Logistics',
  [UserInterests.CommercialLoans]: 'Commercial Loans',
  [UserInterests.PayrollAndHr]: 'Payroll and HR',
  [UserInterests.Payments]: 'Payments'
};

export const coreTransactionPaymentTypeColorPicker = (type: CoreTransactionPaymentType) => {
  switch (type) {
    case CoreTransactionPaymentType.ACH:
      return '#dc8c08';
    case CoreTransactionPaymentType.ACHWIRE:
      return '#414288';
    case CoreTransactionPaymentType.Adjustment:
      return '#682d63';
    case CoreTransactionPaymentType.BillPayments:
      return '#5FB49C';
    case CoreTransactionPaymentType.Card:
      return '#d8f4e7';
    case CoreTransactionPaymentType.CardCredit:
      return '#98DFAF';
    case CoreTransactionPaymentType.CardDebit:
      return '#DEEFB7';
    case CoreTransactionPaymentType.Cash:
      return '#3581B8';
    case CoreTransactionPaymentType.Check:
      return '#E65F5C';
    case CoreTransactionPaymentType.Crypto:
      return '#0F0326';
    case CoreTransactionPaymentType.Dividend:
      return '#B5D99C';
    case CoreTransactionPaymentType.Fee:
      return '#324376';
    case CoreTransactionPaymentType.InterestDividend:
      return '#F5F7DC';
    case CoreTransactionPaymentType.Interest:
      return '#FFFF82';
    case CoreTransactionPaymentType.MonetaryInstrument:
      return '#F19A3E';
    case CoreTransactionPaymentType.Payments:
      return '#E8FCCF';
    case CoreTransactionPaymentType.P2P:
      return '#96E072';
    case CoreTransactionPaymentType.Physical:
      return '#78A9F5';
    case CoreTransactionPaymentType.ReturnedItems:
      return '#3DA35D';
    case CoreTransactionPaymentType.NSF:
      return '#3E8914';
    case CoreTransactionPaymentType.RTP:
      return '#ff4e50';
    case CoreTransactionPaymentType.Tax:
      return '#211A1D';
    case CoreTransactionPaymentType.Transfer:
      return '#f5a623';
    case CoreTransactionPaymentType.Wire:
      return '#ff4e50';
    case CoreTransactionPaymentType.Other:
      return '#6320EE';
    case CoreTransactionPaymentType.LoanAdvance:
      return '#30B6D3';
    case CoreTransactionPaymentType.Disbursement:
      return '#9753DA';
    case CoreTransactionPaymentType.PaymentReceived:
      return '#2215B5';
    case CoreTransactionPaymentType.Escrow:
      return '#FF9A24';
    case CoreTransactionPaymentType.Payoff:
      return '#00B59F';
    default:
      return '#B3CDD1';
  }
};
